// src/services/reportService.ts
import axios from "axios";
import store from "../../redux/store";

/**
 * Builds headers and resolves <<BASE_URL>> in the API URL
 */
const prepareRequestConfig = (apiUrl: string) => {
  const timestamp = new Date().toISOString();
  const generateNonce = (timestamp: string) => {
    const CryptoJS = require("crypto-js");

    const requestData = {}; // Adjust if actual data is available
    const hash = CryptoJS.SHA256(`${requestData}${timestamp}`);
    return hash.toString(CryptoJS.enc.Hex);
  };
  const nonce = generateNonce(timestamp);

  const baseUrl = process.env.REACT_APP_BASE_URL ?? "";
  const resolvedUrl = apiUrl.replace("<<BASE_URL>>/", baseUrl);

  const { userToken: token, userId, refreshToken } = store.getState().authUser;

  const headers = {
    Authorization: `Bearer ${token}`,
    "X-Nonce": nonce,
    "X-Timestamp": timestamp,
    UserId: userId,
    RefreshToken: refreshToken,
    "Content-Type": "application/json",
  };

  return { resolvedUrl, headers };
};

/**
 * Generic GET with query params
 */
export const fetchApiData = async (
  apiUrl: string,
  queryParams: Record<string, any> = {}
): Promise<any> => {
  try {
    const { resolvedUrl, headers } = prepareRequestConfig(apiUrl);
    const response = await axios.get(resolvedUrl, {
      headers,
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch data from ${apiUrl}`, error);
    throw error;
  }
};

/**
 * Generic POST (used for report download trigger, etc.)
 */
export const postApiData = async (
  apiUrl: string,
  body: Record<string, any> = {}
): Promise<any> => {
  try {
    const { resolvedUrl, headers } = prepareRequestConfig(apiUrl);
    const response = await axios.post(resolvedUrl, body, { headers });
    return response.data;
  } catch (error) {
    console.error(`Failed to post data to ${apiUrl}`, error);
    throw error;
  }
};

/**
 * Generic PATCH
 */
export const patchApiData = async (
  apiUrl: string,
  body: Record<string, any> = {}
): Promise<any> => {
  try {
    const { resolvedUrl, headers } = prepareRequestConfig(apiUrl);
    const response = await axios.patch(resolvedUrl, body, { headers });
    return response.data;
  } catch (error) {
    console.error(`Failed to patch data to ${apiUrl}`, error);
    throw error;
  }
};

/**
 * Generic DELETE with a request body.
 *
 * axios takes a DELETE body via the `data` config key, not as a second
 * positional argument the way post/patch do — hence a dedicated helper rather
 * than reusing postApiData's shape.
 */
export const deleteApiData = async (
  apiUrl: string,
  body: Record<string, any> = {}
): Promise<any> => {
  try {
    const { resolvedUrl, headers } = prepareRequestConfig(apiUrl);
    const response = await axios.delete(resolvedUrl, { headers, data: body });
    return response.data;
  } catch (error) {
    console.error(`Failed to delete data at ${apiUrl}`, error);
    throw error;
  }
};

/**
 * Download report (pdf, xlsx, docx)
 */
export const downloadReport = async (
  reportCode: string,
  contentType: "pdf" | "xlsx" | "docx",
  scope: string,
  params: Record<string, any> = {}
): Promise<Blob | null> => {
  try {
    const baseUrl = process.env.REACT_APP_BASE_URL ?? "";
    const { headers } = prepareRequestConfig(baseUrl);

    headers["Content-Type"] = "application/json";
    const url = `${baseUrl}report/new/${scope}/${reportCode}`;
    const response = await axios.post(url, params, {
      headers,
      responseType: "blob",
    });

    return new Blob([response.data], {
      type: contentType === "pdf" ? "application/pdf" : "application/zip",
    });
  } catch (error) {
    console.error("Failed to download report", error);
    return null;
  }
};
